import axios from "axios";

import constants from "../../constants";

/**
 * Keepo Links: the person's short links on Keepo's short domain (kpo.bio/<path>), backed by the API's
 * `/short-links` endpoints (contract: api-keepo-v2's ShortLinkController and ShortLink::toEditorArray()).
 */

export type ShortLinkStatus = "active" | "paused";

export type ShortLink = {
  /** The link's id (uuid). */
  id: string;
  slug: string;
  /** https://kpo.bio/<slug> */
  shortUrl: string;
  destination: string;
  /** Typed, else the destination page's (null when it has none). */
  title: string | null;
  /** The destination site's icon. */
  favicon: string | null;
  tags: string[];
  status: ShortLinkStatus;
  /** The destination on Google's list of dangerous sites: an "unsafe" link doesn't open. */
  safety: "safe" | "unsafe" | "unknown";
  clicks: number | null;
  /** ISO 8601. */
  createdAt: string | null;
};

/** A page of the list; `tags`: every tag of the person (not only the page's), the most used first. */
export type ShortLinkList = { items: ShortLink[]; total: number; page: number; perPage: number; tags?: string[] };

/** The list's filters: a text (title, path, destination), a period (YYYY-MM-DD) and a tag. */
export type ShortLinkFilters = { q?: string; from?: string; to?: string; tag?: string; page?: number };

/** What the person writes of a link: the destination, the path (empty: random), the title and the tags. */
export type ShortLinkInput = { destination?: string; slug?: string; title?: string; tags?: string[] };

/** The API's error codes for a link. */
export const LINK_ERRORS = {
  invalidDestination: "INVALID_DESTINATION",
  unsafeDestination: "UNSAFE_DESTINATION",
  invalidSlug: "INVALID_SLUG",
  slugTaken: "SLUG_TAKEN",
  slugBlocked: "SLUG_BLOCKED",
  rateLimited: "RATE_LIMITED",
  aiRateLimit: "ERR_AI_RATE_LIMIT",
  notFound: "NOT_FOUND",
  invalidStatus: "INVALID_STATUS",
  cannotSave: "CANNOT_SAVE",
  invalidInput: "INVALID_INPUT",
} as const;

/** The API's code of an error (undefined when it's not one of the API's, e.g. offline). */
export const errorCodeOf = (error: unknown): string | undefined =>
  axios.isAxiosError(error) && typeof error.response?.data?.code === "string" ? error.response.data.code : undefined;

export const listLinks = async (filters: ShortLinkFilters = {}): Promise<ShortLinkList> => {
  const params = Object.fromEntries(
    Object.entries(filters).filter(([, value]) => value !== undefined && value !== "" && value !== null)
  );
  const response = await axios.get(`${constants.api}/short-links`, { params });
  return response.data;
};

export const getLink = async (id: string): Promise<ShortLink> => {
  const response = await axios.get(`${constants.api}/short-links/${encodeURIComponent(id)}`);
  return response.data;
};

export const createLink = async (link: ShortLinkInput): Promise<ShortLink> => {
  const response = await axios.post(`${constants.api}/short-links`, link);
  return response.data;
};

/** Only what is sent changes. */
export const updateLink = async (id: string, link: ShortLinkInput): Promise<ShortLink> => {
  const response = await axios.put(`${constants.api}/short-links/${encodeURIComponent(id)}`, link);
  return response.data;
};

/** Paused: the link shows "not found" (its analytics stay). */
export const setLinkStatus = async (id: string, status: ShortLinkStatus): Promise<ShortLink> => {
  const response = await axios.put(`${constants.api}/short-links/${encodeURIComponent(id)}/status`, { status });
  return response.data;
};

/** The link stops opening; its path is never given to someone else. */
export const deleteLink = async (id: string): Promise<void> => {
  await axios.delete(`${constants.api}/short-links/${encodeURIComponent(id)}`);
};

/**
 * A path for the destination (valid and free) and the destination page's title. "Gerar com IA" asks the AI (else a
 * random path); with `ai: false` (the fields filled when a destination is pasted) the path comes from the page's
 * title or address: about a second instead of several, and nothing of the AI's limit used.
 */
export type LinkSuggestion = { slug: string; title: string | null };

export const suggestSlug = async (destination: string, { ai = true } = {}): Promise<LinkSuggestion> => {
  const response = await axios.post(
    `${constants.api}/short-links/suggest-slug`,
    ai ? { destination } : { destination, ai }
  );
  return { slug: response.data.slug, title: response.data.title ?? null };
};

/** A value of a list of the analytics (a country, a city, a device...) and its clicks. */
export type LinkStatsItem = { value: string; clicks: number; code?: string | null; country?: string | null };

/**
 * The analytics of a link over a period: its clicks of each day, and where they came from. Lists bring the 10 with
 * the most clicks; "unknown" (place, device, system) and "direct" (no site) are the API's words for those.
 */
export type LinkStats = {
  from: string;
  to: string;
  clicks: number;
  days: { date: string; clicks: number }[];
  countries: LinkStatsItem[];
  cities: LinkStatsItem[];
  devices: LinkStatsItem[];
  systems: LinkStatsItem[];
  sources: LinkStatsItem[];
};

/** The analytics of a link from one day to another (YYYY-MM-DD). */
export const getLinkStats = async (id: string, from: string, to: string): Promise<LinkStats> => {
  const response = await axios.get(`${constants.api}/short-links/${encodeURIComponent(id)}/stats`, {
    params: { from, to },
  });
  return response.data;
};
